import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Users,
  Calendar,
  Plus,
  Minus,
  X,
  Info,
  Clock,
  User,
  Eye,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

interface Cashbox {
  id: string;
  ruta: number;
  unit_id: string;
  opened_at: string;
  closed_at?: string;
  opened_by_user_id: string;
  closed_by_user_id?: string;
  initial_cash_amount: number;
  status: 'open' | 'closed';
  mobile_start: boolean;
  final_cash_amount?: number;
}

interface CashMetrics {
  // Caixa Atual
  initialCash: number;
  newSales: number;
  renewedSales: number;
  totalSales: number;
  collections: number;
  income: number;
  expenses: number;
  withdrawals: number;
  availableCash: number;
  
  // Desempenho - Carteira
  portfolioFinal: number;
  portfolioVariation: number;
  portfolioInitial: number;
  portfolioCollected: number;
  
  // Desempenho - Recebimento
  intendedCollection: number;
  clientsToCollect: number;
  actualCollection: number;
  additionalCollection: number;
  compliance: number;
  unitCollectionPercentage: number;
  extraCollection: number;
  unscheduledClients: number;
  
  // Informações - Créditos
  creditsToCollect: number;
  creditsToCollectAmount: number;
  unscheduledCredits: number;
  unscheduledCreditsAmount: number;
  cancelledCredits: number;
  newCredits: number;
  activeCredits: number;
  
  // Informações - Movimento de Créditos
  paidCredits: number;
  unpaidCredits: number;
  synchronizedCredits: number;
  clientEfficiency: number;
}

interface Informacoes {
  sessao: {
    unidade: string;
    trabalhador: string;
    abertura: string;
    fechamento_ou_status: string;
  };
  a_receber: number;
  quitados: number;
  nao_programados: number;
  novos: number;
  ativos: number; // Unique active clients with outstanding balances
  movimento_creditos: {
    pagos: number;
    nao_pagaram: number;
    sincronizados: number;
  };
  eficiencia_clientes_percent: number;
}

interface CashboxHistoryItem extends Cashbox {
  opened_by_user?: {
    email: string;
  };
  closed_by_user?: {
    email: string;
  };
}

interface ContractSold {
  id: string;
  cpf: string;
  name: string;
  contract_value: number;
  financed_amount: number;
  installments_count: number;
  created_at: string;
  type: 'new' | 'renewed';
}

interface PaymentReceived {
  id: string;
  client_name: string;
  client_cpf: string;
  amount: number;
  installment_number: number;
  paid_at: string;
  scheduled: boolean;
  contract_id: string;
}

interface CashMovementDetail {
  id: string;
  kind: string;
  amount: number;
  description: string | null;
  created_at: string;
}

interface HistoricalCashboxData {
  cashbox: CashboxHistoryItem;
  metrics: CashMetrics;
  informacoes: Informacoes;
  contractsSold: ContractSold[];
  paymentsReceived: PaymentReceived[];
  cashMovements: CashMovementDetail[];
}

export default function CaixaV2() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRuta, setSelectedRuta] = useState<number>(1);
  const [currentCashbox, setCurrentCashbox] = useState<Cashbox | null>(null);
  const [unitName, setUnitName] = useState<string>('');
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [cashboxHistory, setCashboxHistory] = useState<CashboxHistoryItem[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showOpenModal, setShowOpenModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [initialCashAmount, setInitialCashAmount] = useState<string>('');
  const [finalCashAmount, setFinalCashAmount] = useState<string>('');
  const [opening, setOpening] = useState(false);
  const [closing, setClosing] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const [expenseAmount, setExpenseAmount] = useState<string>('');
  const [expenseDescription, setExpenseDescription] = useState<string>('');
  const [withdrawalAmount, setWithdrawalAmount] = useState<string>('');
  const [withdrawalDescription, setWithdrawalDescription] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedCashboxForDetails, setSelectedCashboxForDetails] = useState<CashboxHistoryItem | null>(null);
  const [historicalData, setHistoricalData] = useState<HistoricalCashboxData | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [showContractsSection, setShowContractsSection] = useState(true);
  const [showPaymentsSection, setShowPaymentsSection] = useState(true);
  const [showMovementsSection, setShowMovementsSection] = useState(true);

  const [cashMetrics, setCashMetrics] = useState<CashMetrics>({
    initialCash: 0,
    newSales: 0,
    renewedSales: 0,
    totalSales: 0,
    collections: 0,
    income: 0,
    expenses: 0,
    withdrawals: 0,
    availableCash: 0,
    portfolioFinal: 0,
    portfolioVariation: 0,
    portfolioInitial: 0,
    portfolioCollected: 0,
    intendedCollection: 0,
    clientsToCollect: 0,
    actualCollection: 0,
    additionalCollection: 0,
    compliance: 0,
    unitCollectionPercentage: 0,
    extraCollection: 0,
    unscheduledClients: 0,
    creditsToCollect: 0,
    creditsToCollectAmount: 0,
    unscheduledCredits: 0,
    unscheduledCreditsAmount: 0,
    cancelledCredits: 0,
    newCredits: 0,
    activeCredits: 0,
    paidCredits: 0,
    unpaidCredits: 0,
    synchronizedCredits: 0,
    clientEfficiency: 0
  });

  const [informacoes, setInformacoes] = useState<Informacoes>({
    sessao: {
      unidade: '',
      trabalhador: '',
      abertura: '',
      fechamento_ou_status: ''
    },
    a_receber: 0,
    quitados: 0,
    nao_programados: 0,
    novos: 0,
    ativos: 0,
    movimento_creditos: {
      pagos: 0,
      nao_pagaram: 0,
      sincronizados: 0
    },
    eficiencia_clientes_percent: 0
  });

  useEffect(() => {
    if (user) {
      fetchCashboxData();
      fetchUnitName();
    }
  }, [selectedRuta, user]);

  useEffect(() => {
    if (showHistoryModal) {
      fetchCashboxHistory();
    }
  }, [showHistoryModal, selectedRuta, user]);

  const fetchUnitName = async () => {
    if (!user?.unit_id) return;
    
    try {
      const { data, error } = await supabase
        .from('units')
        .select('name')
        .eq('id', user.unit_id)
        .single();

      if (error) throw error;
      setUnitName(data?.name || 'Unidade');
    } catch (err) {
      console.error('Error fetching unit name:', err);
      setUnitName('Unidade');
    }
  };

  const fetchCashboxData = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      setError(null);

      // Get current open cashbox for the ruta
      const { data: openCashbox, error: cashboxError } = await supabase
        .from('cashboxes')
        .select('*')
        .eq('unit_id', user.unit_id)
        .eq('ruta', selectedRuta)
        .eq('status', 'open')
        .maybeSingle();

      if (cashboxError) throw cashboxError;

      setCurrentCashbox(openCashbox);

      if (openCashbox) {
        // Calculate metrics for open cashbox
        await calculateMetrics(openCashbox);
      } else {
        // Reset metrics when no open cashbox
        setCashMetrics({
          initialCash: 0,
          newSales: 0,
          renewedSales: 0,
          totalSales: 0,
          collections: 0,
          income: 0,
          expenses: 0,
          withdrawals: 0,
          availableCash: 0,
          portfolioFinal: 0,
          portfolioVariation: 0,
          portfolioInitial: 0,
          portfolioCollected: 0,
          intendedCollection: 0,
          clientsToCollect: 0,
          actualCollection: 0,
          additionalCollection: 0,
          compliance: 0,
          unitCollectionPercentage: 0,
          extraCollection: 0,
          unscheduledClients: 0,
          creditsToCollect: 0,
          creditsToCollectAmount: 0,
          unscheduledCredits: 0,
          unscheduledCreditsAmount: 0,
          cancelledCredits: 0,
          newCredits: 0,
          activeCredits: 0,
          paidCredits: 0,
          unpaidCredits: 0,
          synchronizedCredits: 0,
          clientEfficiency: 0
        });
      }

      // Update session info
      setInformacoes(prev => ({
        ...prev,
        sessao: {
          unidade: unitName,
          trabalhador: user.email?.split('@')[0] || 'N/A',
          abertura: openCashbox ? new Date(openCashbox.opened_at).toLocaleString('pt-BR') : 'Não aberto',
          fechamento_ou_status: openCashbox ? 'Aberto' : 'Fechado'
        }
      }));

    } catch (err) {
      console.error('Error fetching cashbox data:', err);
      setError('Erro ao carregar dados do caixa');
    } finally {
      setLoading(false);
    }
  };

  const calculateMetrics = async (cashbox: Cashbox) => {
    if (!user) return;

    try {
      const periodStart = new Date(cashbox.opened_at);
      const periodEnd = new Date();

      const { data: installmentsData, error: installmentsError } = await supabase
        .from('installments')
        .select(`
          amount,
          paid_amount,
          status,
          contract:contracts!inner(
            ruta,
            approval_status,
            unit_id
          )
        `)
        .eq('unit_id', user.unit_id)
        .neq('status', 'paid')
        .eq('contracts.ruta', selectedRuta)
        .eq('contracts.approval_status', 'approved')
        .eq('contracts.unit_id', user.unit_id);

      if (installmentsError) throw installmentsError;

      const portfolioFinal = (installmentsData || []).reduce((sum, installment) => {
        const remainingAmount = installment.amount - (installment.paid_amount || 0);
        return sum + remainingAmount;
      }, 0);

      // Fetch cash movements for the period
      const { data: movements, error: movementsError } = await supabase
        .from('cash_movements')
        .select(`
          *,
          contract:contracts(ruta),
          user:users(ruta)
        `)
        .eq('unit_id', user.unit_id)
        .gte('created_at', periodStart.toISOString())
        .lte('created_at', periodEnd.toISOString());

      if (movementsError) throw movementsError;

      // Calculate cash movements by type
      const newSales = (movements || [])
        .filter(m => m.kind === 'new_sale')
        .filter(m => m.contract?.ruta === selectedRuta)
        .reduce((sum, m) => sum + Math.abs(m.amount), 0);

      const renewedSales = (movements || [])
        .filter(m => m.kind === 'renewed_sale')
        .filter(m => m.contract?.ruta === selectedRuta)
        .reduce((sum, m) => sum + Math.abs(m.amount), 0);

      const income = (movements || [])
        .filter(m => m.kind === 'entry')
        .filter(m => m.user?.ruta === selectedRuta)
        .reduce((sum, m) => sum + m.amount, 0);

      const expenses = (movements || [])
        .filter(m => m.kind === 'expense')
        .filter(m => m.user?.ruta === selectedRuta)
        .reduce((sum, m) => sum + Math.abs(m.amount), 0);

      const withdrawals = (movements || [])
        .filter(m => m.kind === 'withdrawal')
        .filter(m => m.user?.ruta === selectedRuta)
        .reduce((sum, m) => sum + Math.abs(m.amount), 0);

      // Calculate payments received
      const { data: payments, error: paymentsError } = await supabase
        .from('installments')
        .select(`
          paid_amount,
          contract:contracts(ruta)
        `)
        .eq('unit_id', user.unit_id)
        .not('paid_amount', 'is', null)
        .gte('paid_at', periodStart.toISOString())
        .lte('paid_at', periodEnd.toISOString());

      if (paymentsError) throw paymentsError;

      const totalPayments = (payments || [])
        .filter(p => p.contract?.ruta === selectedRuta)
        .reduce((sum, p) => sum + (p.paid_amount || 0), 0);

      const totalSales = newSales + renewedSales;
      const collections = income + totalPayments;
      const availableCash = cashbox.initial_cash_amount - totalSales + collections - expenses - withdrawals;

      // Calculate active clients (all clients with any outstanding balance for this ruta)
      const { data: activeClientsData, error: activeClientsError } = await supabase
        .from('installments')
        .select(`
          contract:contracts!inner(
            cpf,
            ruta,
            approval_status
          )
        `)
        .eq('unit_id', user.unit_id)
        .neq('status', 'paid')
        .eq('contracts.ruta', selectedRuta)
        .eq('contracts.approval_status', 'approved')
        .eq('contracts.unit_id', user.unit_id);

      if (activeClientsError) throw activeClientsError;

      // Count unique clients (by CPF)
      const uniqueActiveClients = new Set(activeClientsData?.map(i => i.contract.cpf) || []).size;

      // Calculate new contracts since cashbox opened
      const { data: newContractsData, error: newContractsError } = await supabase
        .from('cash_movements')
        .select('contract_id')
        .eq('kind', 'new_sale')
        .eq('unit_id', user.unit_id)
        .gte('created_at', periodStart.toISOString())
        .lte('created_at', periodEnd.toISOString());

      if (newContractsError) throw newContractsError;

      const newContractsCount = new Set(newContractsData?.map(m => m.contract_id).filter(Boolean) || []).size;

      // Calculate clients to collect since cashbox opened (unique clients, not installments)
      const { data: clientsToCollectData, error: clientsToCollectError } = await supabase
        .from('installments')
        .select(`
          contract:contracts!inner(
            cpf,
            ruta,
            approval_status
          )
        `)
        .eq('unit_id', user.unit_id)
        .eq('status', 'pending')
        .gte('due_date', periodStart.toISOString().split('T')[0])
        .eq('contracts.ruta', selectedRuta)
        .eq('contracts.approval_status', 'approved')
        .eq('contracts.unit_id', user.unit_id);

      if (clientsToCollectError) throw clientsToCollectError;

      // Count unique clients (by CPF) to collect
      const clientsToCollectCount = new Set(clientsToCollectData?.map(i => i.contract.cpf) || []).size;

      setCashMetrics({
        initialCash: cashbox.initial_cash_amount,
        newSales,
        renewedSales,
        totalSales,
        collections,
        income,
        expenses,
        withdrawals,
        availableCash,
        portfolioFinal,
        portfolioVariation: 0, // TODO: Implement calculation
        portfolioInitial: 0, // TODO: Implement calculation
        portfolioCollected: collections,
        intendedCollection: 0, // TODO: Implement calculation
        clientsToCollect: 0, // TODO: Implement calculation
        actualCollection: collections,
        additionalCollection: 0, // TODO: Implement calculation
        compliance: 0, // TODO: Implement calculation
        unitCollectionPercentage: 0, // TODO: Implement calculation
        extraCollection: 0, // TODO: Implement calculation
        unscheduledClients: 0, // TODO: Implement calculation
        creditsToCollect: clientsToCollectCount,
        creditsToCollectAmount: 0, // TODO: Implement calculation
        unscheduledCredits: 0, // TODO: Implement calculation
        unscheduledCreditsAmount: 0, // TODO: Implement calculation
        cancelledCredits: 0, // TODO: Implement calculation
        newCredits: newContractsCount,
        activeCredits: uniqueActiveClients,
        paidCredits: 0, // TODO: Implement calculation
        unpaidCredits: 0, // TODO: Implement calculation
        synchronizedCredits: 0, // TODO: Implement calculation
        clientEfficiency: 0 // TODO: Implement calculation
      });

      // Update informacoes with calculated values
      setInformacoes(prev => ({
        ...prev,
        a_receber: clientsToCollectCount,
        quitados: 0, // TODO: Implement calculation for settled clients who didn't renew
        novos: newContractsCount,
        ativos: uniqueActiveClients
      }));

    } catch (err) {
      console.error('Error calculating metrics:', err);
    }
  };

  const fetchCashboxHistory = async () => {
    if (!user) return;

    setLoadingHistory(true);
    try {
      const { data, error } = await supabase
        .from('cashboxes')
        .select(`
          *,
          opened_by_user:opened_by_user_id(email),
          closed_by_user:closed_by_user_id(email)
        `)
        .eq('unit_id', user.unit_id)
        .eq('ruta', selectedRuta)
        .eq('status', 'closed')
        .order('closed_at', { ascending: false });

      if (error) throw error;
      setCashboxHistory(data || []);
    } catch (err) {
      console.error('Error fetching cashbox history:', err);
      setError('Erro ao carregar histórico de caixas');
    } finally {
      setLoadingHistory(false);
    }
  };

  const fetchHistoricalCashboxDetails = async (cashbox: CashboxHistoryItem) => {
    if (!user) return;

    setLoadingDetails(true);
    try {
      const periodStart = new Date(cashbox.opened_at);
      const periodEnd = cashbox.closed_at ? new Date(cashbox.closed_at) : new Date();

      const { data: movements, error: movementsError } = await supabase
        .from('cash_movements')
        .select(`
          *,
          contract:contracts(ruta, cpf, name, contract_value, financed_amount, installments_count),
          user:users(ruta)
        `)
        .eq('unit_id', user.unit_id)
        .gte('created_at', periodStart.toISOString())
        .lte('created_at', periodEnd.toISOString());

      if (movementsError) throw movementsError;

      const newSales = (movements || [])
        .filter(m => m.kind === 'new_sale')
        .filter(m => m.contract?.ruta === selectedRuta)
        .reduce((sum, m) => sum + Math.abs(m.amount), 0);

      const renewedSales = (movements || [])
        .filter(m => m.kind === 'renewed_sale')
        .filter(m => m.contract?.ruta === selectedRuta)
        .reduce((sum, m) => sum + Math.abs(m.amount), 0);

      const income = (movements || [])
        .filter(m => m.kind === 'entry')
        .filter(m => m.user?.ruta === selectedRuta)
        .reduce((sum, m) => sum + m.amount, 0);

      const expenses = (movements || [])
        .filter(m => m.kind === 'expense')
        .filter(m => m.user?.ruta === selectedRuta)
        .reduce((sum, m) => sum + Math.abs(m.amount), 0);

      const withdrawals = (movements || [])
        .filter(m => m.kind === 'withdrawal')
        .filter(m => m.user?.ruta === selectedRuta)
        .reduce((sum, m) => sum + Math.abs(m.amount), 0);

      const { data: payments, error: paymentsError } = await supabase
        .from('installments')
        .select(`
          id,
          paid_amount,
          installment_number,
          paid_at,
          contract:contracts!inner(
            id,
            ruta,
            cpf,
            name
          )
        `)
        .eq('unit_id', user.unit_id)
        .not('paid_amount', 'is', null)
        .gte('paid_at', periodStart.toISOString())
        .lte('paid_at', periodEnd.toISOString())
        .eq('contracts.ruta', selectedRuta);

      if (paymentsError) throw paymentsError;

      const totalPayments = (payments || [])
        .reduce((sum, p) => sum + (p.paid_amount || 0), 0);

      const totalSales = newSales + renewedSales;
      const collections = income + totalPayments;
      const availableCash = cashbox.initial_cash_amount - totalSales + collections - expenses - withdrawals;

      const contractsSold: ContractSold[] = (movements || [])
        .filter(m => (m.kind === 'new_sale' || m.kind === 'renewed_sale') && m.contract?.ruta === selectedRuta)
        .map(m => ({
          id: m.contract_id || '',
          cpf: m.contract?.cpf || '',
          name: m.contract?.name || '',
          contract_value: m.contract?.contract_value || 0,
          financed_amount: m.contract?.financed_amount || 0,
          installments_count: m.contract?.installments_count || 0,
          created_at: m.created_at,
          type: m.kind === 'new_sale' ? 'new' : 'renewed'
        }));

      const paymentsReceived: PaymentReceived[] = (payments || [])
        .map(p => ({
          id: p.id,
          client_name: p.contract?.name || '',
          client_cpf: p.contract?.cpf || '',
          amount: p.paid_amount || 0,
          installment_number: p.installment_number,
          paid_at: p.paid_at || '',
          scheduled: true,
          contract_id: p.contract?.id || ''
        }));

      const cashMovements: CashMovementDetail[] = (movements || [])
        .filter(m => m.user?.ruta === selectedRuta || m.contract?.ruta === selectedRuta)
        .map(m => ({
          id: m.id,
          kind: m.kind,
          amount: m.amount,
          description: m.description,
          created_at: m.created_at
        }))
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

      const { data: installmentsData, error: installmentsError } = await supabase
        .from('installments')
        .select(`
          amount,
          paid_amount,
          status,
          contract:contracts!inner(
            ruta,
            approval_status,
            unit_id
          )
        `)
        .eq('unit_id', user.unit_id)
        .neq('status', 'paid')
        .eq('contracts.ruta', selectedRuta)
        .eq('contracts.approval_status', 'approved')
        .eq('contracts.unit_id', user.unit_id);

      if (installmentsError) throw installmentsError;

      const portfolioFinal = (installmentsData || []).reduce((sum, installment) => {
        const remainingAmount = installment.amount - (installment.paid_amount || 0);
        return sum + remainingAmount;
      }, 0);

      const { data: activeClientsData, error: activeClientsError } = await supabase
        .from('installments')
        .select(`
          contract:contracts!inner(
            cpf,
            ruta,
            approval_status
          )
        `)
        .eq('unit_id', user.unit_id)
        .neq('status', 'paid')
        .eq('contracts.ruta', selectedRuta)
        .eq('contracts.approval_status', 'approved')
        .eq('contracts.unit_id', user.unit_id);

      if (activeClientsError) throw activeClientsError;

      const uniqueActiveClients = new Set(activeClientsData?.map(i => i.contract.cpf) || []).size;
      const newContractsCount = contractsSold.filter(c => c.type === 'new').length;

      const metrics: CashMetrics = {
        initialCash: cashbox.initial_cash_amount,
        newSales,
        renewedSales,
        totalSales,
        collections,
        income,
        expenses,
        withdrawals,
        availableCash,
        portfolioFinal,
        portfolioVariation: 0,
        portfolioInitial: 0,
        portfolioCollected: collections,
        intendedCollection: 0,
        clientsToCollect: 0,
        actualCollection: collections,
        additionalCollection: 0,
        compliance: 0,
        unitCollectionPercentage: 0,
        extraCollection: 0,
        unscheduledClients: 0,
        creditsToCollect: 0,
        creditsToCollectAmount: 0,
        unscheduledCredits: 0,
        unscheduledCreditsAmount: 0,
        cancelledCredits: 0,
        newCredits: newContractsCount,
        activeCredits: uniqueActiveClients,
        paidCredits: paymentsReceived.length,
        unpaidCredits: 0,
        synchronizedCredits: 0,
        clientEfficiency: 0
      };

      const info: Informacoes = {
        sessao: {
          unidade: unitName,
          trabalhador: cashbox.opened_by_user?.email?.split('@')[0] || 'N/A',
          abertura: new Date(cashbox.opened_at).toLocaleString('pt-BR'),
          fechamento_ou_status: cashbox.closed_at ? new Date(cashbox.closed_at).toLocaleString('pt-BR') : 'N/A'
        },
        a_receber: 0,
        quitados: 0,
        nao_programados: 0,
        novos: newContractsCount,
        ativos: uniqueActiveClients,
        movimento_creditos: {
          pagos: paymentsReceived.length,
          nao_pagaram: 0,
          sincronizados: 0
        },
        eficiencia_clientes_percent: 0
      };

      setHistoricalData({
        cashbox,
        metrics,
        informacoes: info,
        contractsSold,
        paymentsReceived,
        cashMovements
      });

      setShowDetailsModal(true);
    } catch (err) {
      console.error('Error fetching historical cashbox details:', err);
      setError('Erro ao carregar detalhes do caixa');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleOpenCashbox = async () => {
    if (!user) return;

    setOpening(true);
    try {
      const { error } = await supabase
        .from('cashboxes')
        .insert({
          ruta: selectedRuta,
          unit_id: user.unit_id,
          opened_by_user_id: user.id,
          initial_cash_amount: parseFloat(initialCashAmount) || 0,
          status: 'open',
          mobile_start: false
        });

      if (error) throw error;

      setShowOpenModal(false);
      setInitialCashAmount('');
      await fetchCashboxData();
    } catch (err) {
      console.error('Error opening cashbox:', err);
      setError('Erro ao abrir caixa');
    } finally {
      setOpening(false);
    }
  };

  const handleCloseCashbox = async () => {
    if (!currentCashbox || !user) return;

    setClosing(true);
    try {
      const { error } = await supabase
        .from('cashboxes')
        .update({
          status: 'closed',
          closed_at: new Date().toISOString(),
          closed_by_user_id: user.id,
          final_cash_amount: parseFloat(finalCashAmount) || caixaAtual.saldo_atual
        })
        .eq('id', currentCashbox.id);

      if (error) throw error;

      setShowCloseModal(false);
      setFinalCashAmount('');
      await fetchCashboxData();
    } catch (err) {
      console.error('Error closing cashbox:', err);
      setError('Erro ao fechar caixa');
    } finally {
      setClosing(false);
    }
  };

  const handleAddExpense = async () => {
    if (!user || !currentCashbox) return;

    setSubmitting(true);
    try {
      const { error } = await supabase
        .from('cash_movements')
        .insert({
          kind: 'expense',
          amount: -Math.abs(parseFloat(expenseAmount)),
          description: expenseDescription,
          unit_id: user.unit_id,
          user_id: user.id,
          created_at: new Date().toISOString()
        });

      if (error) throw error;

      setShowExpenseModal(false);
      setExpenseAmount('');
      setExpenseDescription('');
      await fetchCashboxData();
    } catch (err) {
      console.error('Error adding expense:', err);
      setError('Erro ao adicionar gasto');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddWithdrawal = async () => {
    if (!user || !currentCashbox) return;

    setSubmitting(true);
    try {
      const { error } = await supabase
        .from('cash_movements')
        .insert({
          kind: 'withdrawal',
          amount: -Math.abs(parseFloat(withdrawalAmount)),
          description: withdrawalDescription,
          unit_id: user.unit_id,
          user_id: user.id,
          created_at: new Date().toISOString()
        });

      if (error) throw error;

      setShowWithdrawalModal(false);
      setWithdrawalAmount('');
      setWithdrawalDescription('');
      await fetchCashboxData();
    } catch (err) {
      console.error('Error adding withdrawal:', err);
      setError('Erro ao adicionar retirada');
    } finally {
      setSubmitting(false);
    }
  };

  const prepareOpenCashbox = async () => {
    if (!user) return;

    try {
      // Get the last closed cashbox to determine initial amount
      const { data: lastCashbox, error } = await supabase
        .from('cashboxes')
        .select('final_cash_amount')
        .eq('unit_id', user.unit_id)
        .eq('ruta', selectedRuta)
        .eq('status', 'closed')
        .order('closed_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;

      const suggestedAmount = lastCashbox?.final_cash_amount || 0;
      setInitialCashAmount(suggestedAmount.toString());
      setShowOpenModal(true);
    } catch (err) {
      console.error('Error preparing cashbox opening:', err);
      setInitialCashAmount('0');
      setShowOpenModal(true);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDuration = (openedAt: string, closedAt?: string) => {
    const start = new Date(openedAt);
    const end = closedAt ? new Date(closedAt) : new Date();
    const diffMs = end.getTime() - start.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${diffHours}h ${diffMinutes}m`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Caixa V2</h1>
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700">Selecionar Ruta:</label>
            <select
              value={selectedRuta}
              onChange={(e) => setSelectedRuta(parseInt(e.target.value))}
              className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-sm"
            >
              <option value={1}>Ruta 1</option>
              <option value={2}>Ruta 2</option>
              <option value={3}>Ruta 3</option>
              <option value={4}>Ruta 4</option>
              <option value={5}>Ruta 5</option>
              <option value={6}>Ruta 6</option>
              <option value={7}>Ruta 7</option>
              <option value={8}>Ruta 8</option>
            </select>
            
            <button
              onClick={() => setShowHistoryModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              <Info className="w-4 h-4" />
              Ver Histórico
            </button>

            {currentCashbox ? (
              <button
                onClick={() => {
                  setFinalCashAmount(cashMetrics.availableCash.toString());
                  setShowCloseModal(true);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
              >
                <X className="w-4 h-4" />
                Fechar Caixa
              </button>
            ) : (
              <button
                onClick={prepareOpenCashbox}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Abrir Caixa
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-md">
            {error}
          </div>
        )}

        {/* Cashbox Status Info */}
        {currentCashbox && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-blue-800">
                  Caixa Aberto - Ruta {currentCashbox.ruta}
                </h3>
                <p className="text-xs text-blue-600">
                  Aberto em: {new Date(currentCashbox.opened_at).toLocaleDateString('pt-BR')} às {new Date(currentCashbox.opened_at).toLocaleTimeString('pt-BR')}
                  {currentCashbox.mobile_start && ' (Iniciado pelo celular)'}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium text-blue-800">
                  Caixa Inicial: {formatCurrency(currentCashbox.initial_cash_amount)}
                </p>
                <p className="text-xs text-blue-600">
                  Duração: {formatDuration(currentCashbox.opened_at)}
                </p>
              </div>
            </div>
          </div>
        )}

        {!currentCashbox && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-yellow-600" />
              <p className="text-yellow-800">
                Nenhum caixa aberto para a Ruta {selectedRuta}. Clique em "Abrir Caixa" para iniciar uma nova sessão.
              </p>
            </div>
          </div>
        )}

        {/* Three Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Left Column - Caixa Atual (Purple) */}
          <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-xl shadow-lg p-6 text-white">
            <h2 className="text-xl font-bold mb-6">Caixa Atual</h2>
            
            {/* Main Balance Display */}
            <div className="mb-8">
              <div className="text-center">
                <p className="text-sm opacity-80 mb-2">BRL R$</p>
                <p className="text-4xl font-bold mb-2">
                  {formatCurrency(cashMetrics.availableCash).replace('R$', '').trim()}
                </p>
                <p className="text-xs opacity-70">
                  {new Date().toLocaleDateString('pt-BR')} {new Date().toLocaleTimeString('pt-BR')}
                </p>
              </div>
            </div>

            {/* Cash Flow Breakdown */}
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm">Caixa Inicial</span>
                <span className="text-green-300 font-medium">
                  {formatCurrency(cashMetrics.initialCash)}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm">Novas Vendas</span>
                <span className="text-red-300 font-medium">
                  -{formatCurrency(cashMetrics.newSales)}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm">Vendas Renovadas</span>
                <span className="text-red-300 font-medium">
                  -{formatCurrency(cashMetrics.renewedSales)}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm">Total de Vendas</span>
                <span className="text-red-300 font-medium">
                  -{formatCurrency(cashMetrics.totalSales)}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm">Recebimento</span>
                <span className="text-green-300 font-medium">
                  +{formatCurrency(cashMetrics.collections)}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm">Entradas Extras</span>
                <span className="text-green-300 font-medium">
                  +{formatCurrency(cashMetrics.income)}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm">Gastos</span>
                <span className="text-red-300 font-medium">
                  -{formatCurrency(cashMetrics.expenses)}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm">Retiradas</span>
                <span className="text-red-300 font-medium">
                  -{formatCurrency(cashMetrics.withdrawals)}
                </span>
              </div>
            </div>

            {currentCashbox && (
              <div className="mt-8 pt-6 border-t border-purple-400">
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setShowExpenseModal(true)}
                    className="flex items-center justify-center gap-2 px-4 py-3 bg-white bg-opacity-20 text-white rounded-lg hover:bg-opacity-30 transition-colors"
                  >
                    <Minus className="w-4 h-4" />
                    <span className="text-sm">Gasto</span>
                  </button>
                  <button
                    onClick={() => setShowWithdrawalModal(true)}
                    className="flex items-center justify-center gap-2 px-4 py-3 bg-white bg-opacity-20 text-white rounded-lg hover:bg-opacity-30 transition-colors"
                  >
                    <Minus className="w-4 h-4" />
                    <span className="text-sm">Retirada</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Middle Column - Desempenho (Yellow/Orange) */}
          <div className="bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl shadow-lg p-6 text-white">
            <h2 className="text-xl font-bold mb-6">Desempenho</h2>
            
            {/* Portfolio Section */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4 border-b border-yellow-300 pb-2">Carteira</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm">Final</span>
                  <span className="text-green-200 font-medium">
                    {formatCurrency(cashMetrics.portfolioFinal)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Variação</span>
                  <span className="text-green-200 font-medium">
                    {(cashMetrics.portfolioVariation).toFixed(2)}%
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Inicial</span>
                  <span className="text-white font-medium">
                    {formatCurrency(cashMetrics.portfolioInitial)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Carteira Recebida</span>
                  <span className="text-white font-medium">
                    {formatCurrency(cashMetrics.portfolioCollected)}
                  </span>
                </div>
              </div>
            </div>

            {/* Collections Section */}
            <div>
              <h3 className="text-lg font-semibold mb-4 border-b border-yellow-300 pb-2">Recebimento</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm">Pretendido</span>
                  <span className="text-white font-medium">
                    {formatCurrency(cashMetrics.intendedCollection)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Clientes a receber</span>
                  <span className="text-white font-medium">
                    {cashMetrics.clientsToCollect}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Recebimento</span>
                  <span className="text-white font-medium">
                    {formatCurrency(cashMetrics.actualCollection)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Recebimento Adicional</span>
                  <span className="text-green-200 font-medium">
                    {formatCurrency(cashMetrics.additionalCollection)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Cumprimento</span>
                  <span className="text-green-200 font-medium">
                    {(cashMetrics.compliance).toFixed(2)}%
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">% Recebimento da Unidade</span>
                  <span className="text-white font-medium">
                    {(cashMetrics.unitCollectionPercentage).toFixed(2)}%
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Recebimento Extra</span>
                  <span className="text-green-200 font-medium">
                    {formatCurrency(cashMetrics.extraCollection)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Clientes Não Programados</span>
                  <span className="text-white font-medium">
                    {cashMetrics.unscheduledClients}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Informações (Blue) */}
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white">
            <h2 className="text-xl font-bold mb-6">Informações</h2>
            
            {/* Session Info */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4">Sessão do Caixa</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Unidade:</span>
                  <span>{informacoes.sessao.unidade}</span>
                </div>
                <div className="flex justify-between">
                  <span>Trabalhador:</span>
                  <span>{informacoes.sessao.trabalhador}</span>
                </div>
                <div className="flex justify-between">
                  <span>Abertura:</span>
                  <span className="text-xs">{informacoes.sessao.abertura}</span>
                </div>
                <div className="flex justify-between">
                  <span>Status:</span>
                  <span className={currentCashbox ? 'text-green-300' : 'text-red-300'}>
                    {informacoes.sessao.fechamento_ou_status}
                  </span>
                </div>
              </div>
            </div>

            {/* Credits Info */}
            <div>
              <h3 className="text-lg font-semibold mb-4 border-b border-blue-300 pb-2">Créditos</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm">A Receber</span>
                  <span className="text-white font-medium">{informacoes.a_receber}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Quitados</span>
                  <span className="text-red-200 font-medium">{informacoes.quitados}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Não Programados</span>
                  <span className="text-green-200 font-medium">{informacoes.nao_programados}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Novos</span>
                  <span className="text-green-200 font-medium">{informacoes.novos}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Ativos</span>
                  <span className="text-white font-medium">{informacoes.ativos}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Open Cashbox Modal */}
        {showOpenModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-lg p-6 max-w-md w-full">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Abrir Caixa - Ruta {selectedRuta}</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valor Inicial do Caixa
                  </label>
                  <input
                    type="number"
                    value={initialCashAmount}
                    onChange={(e) => setInitialCashAmount(e.target.value)}
                    step="0.01"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Valor sugerido baseado no último fechamento
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowOpenModal(false);
                    setInitialCashAmount('');
                  }}
                  className="px-4 py-2 text-gray-700 hover:text-gray-900"
                  disabled={opening}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleOpenCashbox}
                  disabled={opening}
                  className={`px-4 py-2 rounded-md text-white transition-colors ${
                    opening
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  {opening ? 'Abrindo...' : 'Abrir Caixa'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Close Cashbox Modal */}
        {showCloseModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-lg p-6 max-w-md w-full">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Fechar Caixa - Ruta {selectedRuta}</h2>
              
              <div className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Resumo da Sessão</h3>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span>Caixa Inicial:</span>
                      <span>{formatCurrency(cashMetrics.initialCash)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Saldo Calculado:</span>
                      <span className={cashMetrics.availableCash >= 0 ? 'text-green-600' : 'text-red-600'}>
                        {formatCurrency(cashMetrics.availableCash)}
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valor Final do Caixa (Contagem Manual)
                  </label>
                  <input
                    type="number"
                    value={finalCashAmount}
                    onChange={(e) => setFinalCashAmount(e.target.value)}
                    step="0.01"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Conte o dinheiro físico e insira o valor real
                  </p>
                </div>

                {parseFloat(finalCashAmount) !== cashMetrics.availableCash && finalCashAmount && (
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                    <p className="text-sm text-yellow-800">
                      <strong>Diferença detectada:</strong> {formatCurrency(parseFloat(finalCashAmount) - cashMetrics.availableCash)}
                    </p>
                    <p className="text-xs text-yellow-600 mt-1">
                      O valor manual difere do saldo calculado
                    </p>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowCloseModal(false);
                    setFinalCashAmount('');
                  }}
                  className="px-4 py-2 text-gray-700 hover:text-gray-900"
                  disabled={closing}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleCloseCashbox}
                  disabled={closing || !finalCashAmount}
                  className={`px-4 py-2 rounded-md text-white transition-colors ${
                    closing || !finalCashAmount
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {closing ? 'Fechando...' : 'Fechar Caixa'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Expense Modal */}
        {showExpenseModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-lg p-6 max-w-md w-full">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Adicionar Gasto</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descrição
                  </label>
                  <input
                    type="text"
                    value={expenseDescription}
                    onChange={(e) => setExpenseDescription(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Ex: Combustível, Almoço"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valor
                  </label>
                  <input
                    type="number"
                    value={expenseAmount}
                    onChange={(e) => setExpenseAmount(e.target.value)}
                    step="0.01"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                </div>

                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  <p className="text-sm text-yellow-800">
                    Este valor será subtraído do caixa atual.
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowExpenseModal(false);
                    setExpenseAmount('');
                    setExpenseDescription('');
                  }}
                  className="px-4 py-2 text-gray-700 hover:text-gray-900"
                  disabled={submitting}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleAddExpense}
                  disabled={submitting || !expenseAmount || !expenseDescription}
                  className={`px-4 py-2 rounded-md text-white transition-colors ${
                    submitting || !expenseAmount || !expenseDescription
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {submitting ? 'Adicionando...' : 'Adicionar Gasto'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Withdrawal Modal */}
        {showWithdrawalModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-lg p-6 max-w-md w-full">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Adicionar Retirada</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descrição
                  </label>
                  <input
                    type="text"
                    value={withdrawalDescription}
                    onChange={(e) => setWithdrawalDescription(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Ex: Pagamento fornecedor"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valor
                  </label>
                  <input
                    type="number"
                    value={withdrawalAmount}
                    onChange={(e) => setWithdrawalAmount(e.target.value)}
                    step="0.01"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                </div>

                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  <p className="text-sm text-yellow-800">
                    Este valor será subtraído do caixa atual.
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowWithdrawalModal(false);
                    setWithdrawalAmount('');
                    setWithdrawalDescription('');
                  }}
                  className="px-4 py-2 text-gray-700 hover:text-gray-900"
                  disabled={submitting}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleAddWithdrawal}
                  disabled={submitting || !withdrawalAmount || !withdrawalDescription}
                  className={`px-4 py-2 rounded-md text-white transition-colors ${
                    submitting || !withdrawalAmount || !withdrawalDescription
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {submitting ? 'Adicionando...' : 'Adicionar Retirada'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* History Modal */}
        {showHistoryModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-lg p-6 max-w-6xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-800">Histórico de Caixas - Ruta {selectedRuta}</h2>
                <button
                  onClick={() => setShowHistoryModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {loadingHistory ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : cashboxHistory.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-lg">Nenhum histórico encontrado</p>
                  <p className="text-sm">Não há caixas fechados para esta ruta.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Data de Abertura
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Data de Fechamento
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Duração
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Caixa Inicial
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Caixa Final
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Variação
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Aberto Por
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Fechado Por
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Origem
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {cashboxHistory.map((cashbox) => {
                        const variation = (cashbox.final_cash_amount || 0) - cashbox.initial_cash_amount;
                        return (
                          <tr key={cashbox.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {new Date(cashbox.opened_at).toLocaleDateString('pt-BR')}
                              <br />
                              <span className="text-xs text-gray-500">
                                {new Date(cashbox.opened_at).toLocaleTimeString('pt-BR')}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {cashbox.closed_at ? (
                                <>
                                  {new Date(cashbox.closed_at).toLocaleDateString('pt-BR')}
                                  <br />
                                  <span className="text-xs text-gray-500">
                                    {new Date(cashbox.closed_at).toLocaleTimeString('pt-BR')}
                                  </span>
                                </>
                              ) : (
                                'N/A'
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {cashbox.closed_at ? formatDuration(cashbox.opened_at, cashbox.closed_at) : 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatCurrency(cashbox.initial_cash_amount)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {cashbox.final_cash_amount ? formatCurrency(cashbox.final_cash_amount) : 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              <span className={variation >= 0 ? 'text-green-600' : 'text-red-600'}>
                                {variation >= 0 ? '+' : ''}{formatCurrency(variation)}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {cashbox.opened_by_user?.email || 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {cashbox.closed_by_user?.email || 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {cashbox.mobile_start ? 'Mobile' : 'Desktop'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <button
                                onClick={() => fetchHistoricalCashboxDetails(cashbox)}
                                className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                                title="Ver detalhes"
                              >
                                <Eye className="w-4 h-4" />
                                Ver Detalhes
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Detailed Cashbox View Modal */}
        {showDetailsModal && historicalData && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-lg w-full max-w-[95vw] max-h-[95vh] overflow-y-auto">
              {loadingDetails ? (
                <div className="flex items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <>
                  {/* Header */}
                  <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-800">
                        Detalhes do Caixa - Ruta {historicalData.cashbox.ruta}
                      </h2>
                      <p className="text-sm text-gray-600 mt-1">
                        {new Date(historicalData.cashbox.opened_at).toLocaleString('pt-BR')} - {historicalData.cashbox.closed_at ? new Date(historicalData.cashbox.closed_at).toLocaleString('pt-BR') : 'Em aberto'}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setShowDetailsModal(false);
                        setHistoricalData(null);
                      }}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <X className="w-6 h-6" />
                    </button>
                  </div>

                  <div className="p-6">
                    {/* Session Summary */}
                    <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                      <h3 className="text-lg font-semibold text-gray-800 mb-3">Resumo da Sessão</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <p className="text-xs text-gray-600">Unidade</p>
                          <p className="text-sm font-medium text-gray-900">{historicalData.informacoes.sessao.unidade}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">Trabalhador</p>
                          <p className="text-sm font-medium text-gray-900">{historicalData.informacoes.sessao.trabalhador}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">Duração</p>
                          <p className="text-sm font-medium text-gray-900">
                            {historicalData.cashbox.closed_at ? formatDuration(historicalData.cashbox.opened_at, historicalData.cashbox.closed_at) : 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">Origem</p>
                          <p className="text-sm font-medium text-gray-900">
                            {historicalData.cashbox.mobile_start ? 'Mobile' : 'Desktop'}
                          </p>
                        </div>
                      </div>

                      {historicalData.cashbox.final_cash_amount && (
                        <div className="mt-4 pt-4 border-t border-blue-200">
                          <div className="grid grid-cols-3 gap-4">
                            <div>
                              <p className="text-xs text-gray-600">Caixa Inicial</p>
                              <p className="text-sm font-medium text-green-600">
                                {formatCurrency(historicalData.cashbox.initial_cash_amount)}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-600">Saldo Calculado</p>
                              <p className="text-sm font-medium text-gray-900">
                                {formatCurrency(historicalData.metrics.availableCash)}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-600">Caixa Final Contado</p>
                              <p className="text-sm font-medium text-blue-600">
                                {formatCurrency(historicalData.cashbox.final_cash_amount)}
                              </p>
                            </div>
                          </div>
                          {Math.abs(historicalData.cashbox.final_cash_amount - historicalData.metrics.availableCash) > 0.01 && (
                            <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded">
                              <p className="text-xs text-yellow-800">
                                <strong>Diferença:</strong> {formatCurrency(historicalData.cashbox.final_cash_amount - historicalData.metrics.availableCash)}
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Three Column Layout - Metrics */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                      {/* Left Column - Caixa Atual */}
                      <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-xl shadow-lg p-6 text-white">
                        <h3 className="text-xl font-bold mb-6">Caixa Atual</h3>

                        <div className="mb-8">
                          <div className="text-center">
                            <p className="text-sm opacity-80 mb-2">BRL R$</p>
                            <p className="text-4xl font-bold mb-2">
                              {formatCurrency(historicalData.metrics.availableCash).replace('R$', '').trim()}
                            </p>
                          </div>
                        </div>

                        <div className="space-y-4">
                          <div className="flex justify-between items-center">
                            <span className="text-sm">Caixa Inicial</span>
                            <span className="text-green-300 font-medium">
                              {formatCurrency(historicalData.metrics.initialCash)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm">Novas Vendas</span>
                            <span className="text-red-300 font-medium">
                              -{formatCurrency(historicalData.metrics.newSales)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm">Vendas Renovadas</span>
                            <span className="text-red-300 font-medium">
                              -{formatCurrency(historicalData.metrics.renewedSales)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm">Total de Vendas</span>
                            <span className="text-red-300 font-medium">
                              -{formatCurrency(historicalData.metrics.totalSales)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm">Recebimento</span>
                            <span className="text-green-300 font-medium">
                              +{formatCurrency(historicalData.metrics.collections)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm">Entradas Extras</span>
                            <span className="text-green-300 font-medium">
                              +{formatCurrency(historicalData.metrics.income)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm">Gastos</span>
                            <span className="text-red-300 font-medium">
                              -{formatCurrency(historicalData.metrics.expenses)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm">Retiradas</span>
                            <span className="text-red-300 font-medium">
                              -{formatCurrency(historicalData.metrics.withdrawals)}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Middle Column - Desempenho */}
                      <div className="bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl shadow-lg p-6 text-white">
                        <h3 className="text-xl font-bold mb-6">Desempenho</h3>

                        <div className="mb-8">
                          <h4 className="text-lg font-semibold mb-4 border-b border-yellow-300 pb-2">Carteira</h4>
                          <div className="space-y-3">
                            <div className="flex justify-between items-center">
                              <span className="text-sm">Final</span>
                              <span className="text-green-200 font-medium">
                                {formatCurrency(historicalData.metrics.portfolioFinal)}
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-sm">Carteira Recebida</span>
                              <span className="text-white font-medium">
                                {formatCurrency(historicalData.metrics.portfolioCollected)}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div>
                          <h4 className="text-lg font-semibold mb-4 border-b border-yellow-300 pb-2">Recebimento</h4>
                          <div className="space-y-3">
                            <div className="flex justify-between items-center">
                              <span className="text-sm">Recebimento</span>
                              <span className="text-white font-medium">
                                {formatCurrency(historicalData.metrics.actualCollection)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Right Column - Informações */}
                      <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white">
                        <h3 className="text-xl font-bold mb-6">Informações</h3>

                        <div>
                          <h4 className="text-lg font-semibold mb-4 border-b border-blue-300 pb-2">Créditos</h4>
                          <div className="space-y-3">
                            <div className="flex justify-between items-center">
                              <span className="text-sm">Novos</span>
                              <span className="text-green-200 font-medium">{historicalData.informacoes.novos}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-sm">Ativos</span>
                              <span className="text-white font-medium">{historicalData.informacoes.ativos}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-sm">Pagos</span>
                              <span className="text-white font-medium">{historicalData.informacoes.movimento_creditos.pagos}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Contracts Sold Section */}
                    <div className="mb-6">
                      <button
                        onClick={() => setShowContractsSection(!showContractsSection)}
                        className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <h3 className="text-lg font-semibold text-gray-800">
                          Contratos Vendidos ({historicalData.contractsSold.length})
                        </h3>
                        {showContractsSection ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </button>

                      {showContractsSection && historicalData.contractsSold.length > 0 && (
                        <div className="mt-4 space-y-4">
                          {/* New Contracts */}
                          <div>
                            <h4 className="text-md font-semibold text-gray-700 mb-2">
                              Novos Contratos ({historicalData.contractsSold.filter(c => c.type === 'new').length})
                            </h4>
                            <div className="overflow-x-auto">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">CPF</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Valor Contrato</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Financiado</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Parcelas</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {historicalData.contractsSold
                                    .filter(c => c.type === 'new')
                                    .map((contract) => (
                                      <tr key={contract.id}>
                                        <td className="px-4 py-2 text-sm text-gray-900">{contract.name}</td>
                                        <td className="px-4 py-2 text-sm text-gray-900">{contract.cpf}</td>
                                        <td className="px-4 py-2 text-sm text-gray-900">{formatCurrency(contract.contract_value)}</td>
                                        <td className="px-4 py-2 text-sm text-gray-900">{formatCurrency(contract.financed_amount)}</td>
                                        <td className="px-4 py-2 text-sm text-gray-900">{contract.installments_count}</td>
                                        <td className="px-4 py-2 text-sm text-gray-500">
                                          {new Date(contract.created_at).toLocaleString('pt-BR')}
                                        </td>
                                      </tr>
                                    ))}
                                </tbody>
                                <tfoot className="bg-gray-50">
                                  <tr>
                                    <td colSpan={2} className="px-4 py-2 text-sm font-medium text-gray-700">Total</td>
                                    <td className="px-4 py-2 text-sm font-bold text-gray-900">
                                      {formatCurrency(historicalData.contractsSold.filter(c => c.type === 'new').reduce((sum, c) => sum + c.contract_value, 0))}
                                    </td>
                                    <td className="px-4 py-2 text-sm font-bold text-gray-900">
                                      {formatCurrency(historicalData.contractsSold.filter(c => c.type === 'new').reduce((sum, c) => sum + c.financed_amount, 0))}
                                    </td>
                                    <td colSpan={2}></td>
                                  </tr>
                                </tfoot>
                              </table>
                            </div>
                          </div>

                          {/* Renewed Contracts */}
                          {historicalData.contractsSold.filter(c => c.type === 'renewed').length > 0 && (
                            <div>
                              <h4 className="text-md font-semibold text-gray-700 mb-2">
                                Renovações ({historicalData.contractsSold.filter(c => c.type === 'renewed').length})
                              </h4>
                              <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                  <thead className="bg-gray-50">
                                    <tr>
                                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">CPF</th>
                                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Valor Contrato</th>
                                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Financiado</th>
                                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Parcelas</th>
                                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                                    </tr>
                                  </thead>
                                  <tbody className="bg-white divide-y divide-gray-200">
                                    {historicalData.contractsSold
                                      .filter(c => c.type === 'renewed')
                                      .map((contract) => (
                                        <tr key={contract.id}>
                                          <td className="px-4 py-2 text-sm text-gray-900">{contract.name}</td>
                                          <td className="px-4 py-2 text-sm text-gray-900">{contract.cpf}</td>
                                          <td className="px-4 py-2 text-sm text-gray-900">{formatCurrency(contract.contract_value)}</td>
                                          <td className="px-4 py-2 text-sm text-gray-900">{formatCurrency(contract.financed_amount)}</td>
                                          <td className="px-4 py-2 text-sm text-gray-900">{contract.installments_count}</td>
                                          <td className="px-4 py-2 text-sm text-gray-500">
                                            {new Date(contract.created_at).toLocaleString('pt-BR')}
                                          </td>
                                        </tr>
                                      ))}
                                  </tbody>
                                  <tfoot className="bg-gray-50">
                                    <tr>
                                      <td colSpan={2} className="px-4 py-2 text-sm font-medium text-gray-700">Total</td>
                                      <td className="px-4 py-2 text-sm font-bold text-gray-900">
                                        {formatCurrency(historicalData.contractsSold.filter(c => c.type === 'renewed').reduce((sum, c) => sum + c.contract_value, 0))}
                                      </td>
                                      <td className="px-4 py-2 text-sm font-bold text-gray-900">
                                        {formatCurrency(historicalData.contractsSold.filter(c => c.type === 'renewed').reduce((sum, c) => sum + c.financed_amount, 0))}
                                      </td>
                                      <td colSpan={2}></td>
                                    </tr>
                                  </tfoot>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Payments Received Section */}
                    <div className="mb-6">
                      <button
                        onClick={() => setShowPaymentsSection(!showPaymentsSection)}
                        className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <h3 className="text-lg font-semibold text-gray-800">
                          Recebimentos ({historicalData.paymentsReceived.length})
                        </h3>
                        {showPaymentsSection ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </button>

                      {showPaymentsSection && historicalData.paymentsReceived.length > 0 && (
                        <div className="mt-4 overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">CPF</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Valor Pago</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Parcela</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Data/Hora</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {historicalData.paymentsReceived.map((payment) => (
                                <tr key={payment.id}>
                                  <td className="px-4 py-2 text-sm text-gray-900">{payment.client_name}</td>
                                  <td className="px-4 py-2 text-sm text-gray-900">{payment.client_cpf}</td>
                                  <td className="px-4 py-2 text-sm text-green-600 font-medium">
                                    {formatCurrency(payment.amount)}
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-900">{payment.installment_number}</td>
                                  <td className="px-4 py-2 text-sm text-gray-500">
                                    {new Date(payment.paid_at).toLocaleString('pt-BR')}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot className="bg-gray-50">
                              <tr>
                                <td colSpan={2} className="px-4 py-2 text-sm font-medium text-gray-700">Total Recebido</td>
                                <td className="px-4 py-2 text-sm font-bold text-green-600">
                                  {formatCurrency(historicalData.paymentsReceived.reduce((sum, p) => sum + p.amount, 0))}
                                </td>
                                <td colSpan={2}></td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      )}
                    </div>

                    {/* Cash Movements Section */}
                    <div className="mb-6">
                      <button
                        onClick={() => setShowMovementsSection(!showMovementsSection)}
                        className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <h3 className="text-lg font-semibold text-gray-800">
                          Movimentações de Caixa ({historicalData.cashMovements.length})
                        </h3>
                        {showMovementsSection ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </button>

                      {showMovementsSection && historicalData.cashMovements.length > 0 && (
                        <div className="mt-4 space-y-2">
                          {historicalData.cashMovements.map((movement) => {
                            const isPositive = movement.amount > 0;
                            const kindLabel = {
                              'new_sale': 'Nova Venda',
                              'renewed_sale': 'Renovação',
                              'entry': 'Entrada',
                              'expense': 'Gasto',
                              'withdrawal': 'Retirada',
                              'initial': 'Inicial'
                            }[movement.kind] || movement.kind;

                            return (
                              <div key={movement.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                <div className="flex items-center gap-3">
                                  <div className={`w-2 h-2 rounded-full ${isPositive ? 'bg-green-500' : 'bg-red-500'}`}></div>
                                  <div>
                                    <p className="text-sm font-medium text-gray-900">{kindLabel}</p>
                                    {movement.description && (
                                      <p className="text-xs text-gray-500">{movement.description}</p>
                                    )}
                                    <p className="text-xs text-gray-400">
                                      {new Date(movement.created_at).toLocaleString('pt-BR')}
                                    </p>
                                  </div>
                                </div>
                                <div className={`text-sm font-semibold ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                                  {isPositive ? '+' : ''}{formatCurrency(movement.amount)}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}